/**
 * Address arithmetic for reordering one Modbus table of the DCS map.
 *
 * Kept free of React and Immutable so the rules are testable on their own. Addresses are
 * register (or coil) indices; a row occupies [address, address + length).
 */

export interface MapRow {
  id: number;
  address: number;
  length: number;
}

/**
 * Move the row at `from` to `to`, and repack only the rows between the two positions.
 *
 * The span holds the same rows before and after the move, so packing it contiguously from its
 * lowest address never needs more room than it had: rows outside the span never move, and any
 * deliberate gaps outside it survive. Gaps inside the span are closed.
 *
 * Returns the rows in their new order, with new addresses.
 */
export function moveRow (rows: MapRow[], from: number, to: number): MapRow[] {
  if (from === to) {
    return rows;
  }

  const ordered = [...rows];
  const [moved] = ordered.splice(from, 1);
  ordered.splice(to, 0, moved);

  const lo = Math.min(from, to);
  const hi = Math.max(from, to);
  const span = ordered.slice(lo, hi + 1);

  let next = Math.min(...span.map(r => r.address).filter(Number.isFinite));
  const packed = span.map((r) => {
    const placed = { ...r, address: next };
    next += r.length;
    return placed;
  });

  return [...ordered.slice(0, lo), ...packed, ...ordered.slice(hi + 1)];
}

/**
 * Ids of rows that share at least one address with another row, or have no valid address.
 * The API rejects such a map as a whole; this lets the page point at the culprits first.
 */
export function findConflicts (rows: MapRow[]): Set<number> {
  const conflicts = new Set<number>();
  const valid = rows.filter((r) => {
    if (!Number.isInteger(r.address) || r.address < 0) {
      conflicts.add(r.id);
      return false;
    }
    return true;
  });

  const sorted = [...valid].sort((a, b) => a.address - b.address);
  let reach = -1;
  let reachId: number | null = null;
  sorted.forEach((r) => {
    if (r.address < reach) {
      conflicts.add(r.id);
      conflicts.add(reachId as number);
    }
    if (r.address + r.length > reach) {
      reach = r.address + r.length;
      reachId = r.id;
    }
  });

  return conflicts;
}
